import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { BusinessDashboard, AdminDashboard } from '../components/layout/UnifiedDashboard';
import { StoreLogin, StoreDashboard } from '../pages/store/Storelogin';
import StoreActivateTag from "../pages/store/StoreActivateTag";
import { LoginPage, SignupPage } from '../pages/auth';
import {
  OverviewPage, AnalyticsPage, BusinessPage, BatchesPage,
  StoresPage, TagsPage, ProductsPage, SupplyChainPage,
} from '../pages/business';
import ScanConsumerPage from '../pages/consumer/ScanConsumerPage';
import AuthenticScanPage from '../pages/consumer/AuthenticScanPage';
import CertificatePage from '../pages/consumer/CertificatePage';
import ConsumerStoreMapPage from '../pages/consumer/ConsumerStoreMapPage';
import RecallControlPage from '../pages/business/RecallControlPage';
import LotteryResultPage from '../pages/consumer/LotteryResultPage';
import LotteryTestPage from '../pages/consumer/LotteryTestPage';
import ConsumerLotteryPage from '../pages/consumer/ConsumerLotteryPage';
import ViewLottery from '../pages/consumer/ViewLottery';
import MyEntriesPage from '../pages/consumer/MyEntriesPage';
import LotterySpinPage from '../pages/consumer/LotterySpinPage';
import LotteryCertificatePage from '../pages/consumer/LotteryCertificatePage';
import BusinessRegisterPage from '../pages/business/BusinessRegisterPage';
import BusinessLoginPage from '../pages/business/BusinessLoginPage';
import BusinessDashboardPage from '../pages/business/BusinessDashboard';
import BusinessResetPasswordPage from '../pages/business/BusinessResetPasswordPage';
import ActivateTagsPage from "../pages/business/ActivateTagsPage";
import ProductGS1Page from "../pages/business/ProductGS1Page";
import BusinessGS1Page from "../pages/business/BusinessGS1Page";
import { ProfilePage, SecurityPage, SettingsPage, UsersPage } from '../pages/admin';
import { CampaignsPage, MarketingPage, RewardsPage } from '../pages/marketing';
import BlogPublicList from "../pages/public/BlogPublicList";
import BlogPublicDetail from "../pages/public/BlogPublicDetail";
import CaseStudyPublicList from "../pages/public/CaseStudyPublicList";
import CaseStudyPublicDetail from "../pages/public/CaseStudyPublicDetail";
import FAQPage from "../pages/public/FAQPage";
import InvestorPage from "../pages/public/InvestorPage";
import AboutPage from "../pages/public/AboutPage";
import PrivacyPage from "../pages/public/PrivacyPage";
import TermsPage from "../pages/public/TermsPage";
import ContactPage from "../pages/public/ContactPage";
import DeleteDataPage from "../pages/public/DeleteDataPage";
import BusinessShopPage from '../pages/business/BusinessShopPage';
import BlockchainPlansPage from '../pages/business/BlockchainPlansPage';



const useAuth = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('businessToken') || localStorage.getItem('jeptags_token');
  const userType = localStorage.getItem('userType');
  const businessUser = localStorage.getItem('businessUser');
  let userRole: 'business' | 'admin' | null = null;
  if (token && (userType === 'business_user' || businessUser)) userRole = 'business';
  else if (userType === 'admin') userRole = 'admin';
  return {
    isAuthenticated: !!(token && userRole),
    userRole,
    logout: () => {
      ['jeptags_token','user_role','authToken','businessToken','auth_token','token',
       'business','businessData','businessUser','user','userType','refreshToken'].forEach(k => localStorage.removeItem(k));
      sessionStorage.clear();
      window.location.href = '/';
    }
  };
};

const MarketingPageWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  return (
    <MarketingPage
      onGetStarted={() => navigate(isAuthenticated ? '/business/overview' : '/business/register')}
      onScheduleDemo={() => window.open('https://calendly.com/your-booking-link', '_blank')}
    />
  );
};

const QRGeneratorPage = () => (
  <div className="space-y-6">
    <h2 className="text-3xl font-bold text-gray-900">QR Code Generator</h2>
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <p className="text-gray-600">QR generator coming soon.</p>
    </div>
  </div>
);

const BusinessPendingApprovalPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-900 via-yellow-700 to-orange-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
        <span className="text-yellow-600 text-5xl">⏳</span>
        <h2 className="text-2xl font-bold text-gray-900 mt-4 mb-2">Application Under Review</h2>
        <p className="text-gray-600 mb-6">Your business registration is being reviewed. You'll receive an email once approved.</p>
        <button onClick={() => navigate('/business/login')} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium mb-3">Try Login Again</button>
        <button onClick={() => navigate('/')} className="w-full border border-gray-300 hover:bg-gray-50 py-3 rounded-lg font-medium">Back to Homepage</button>
      </div>
    </div>
  );
};

const AdminOverviewPage = () => (
  <div className="space-y-6">
    <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl p-6 text-white">
      <h1 className="text-2xl font-bold mb-2">Admin Console</h1>
      <p className="text-red-100">Platform administration dashboard</p>
    </div>
  </div>
);

const BusinessManagementPage = () => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">Business Management</h1>
    <p className="text-gray-600">Business management interface.</p>
  </div>
);

const SystemHealthPage = () => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">System Health</h1>
    <p className="text-gray-600">System health monitoring.</p>
  </div>
);

const NotificationsPage = () => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <h1 className="text-2xl font-bold text-gray-900 mb-4">Notifications</h1>
    <p className="text-gray-600">Notifications system.</p>
  </div>
);

interface ProtectedRouteProps { children: React.ReactNode; requiredRole?: 'business' | 'admin'; }
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, userRole } = useAuth();
  if (!isAuthenticated) {
    if (requiredRole === 'business') return <Navigate to="/business/login" replace />;
    if (requiredRole === 'admin') return <Navigate to="/admin/login" replace />;
    return <Navigate to="/login" replace />;
  }
  if (requiredRole && userRole !== requiredRole) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
};

const NotFoundPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-8">Page not found</p>
        <div className="space-x-4">
          <button onClick={() => navigate(-1)} className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700">Go Back</button>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">Home</button>
        </div>
      </div>
    </div>
  );
};

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 mb-4">403</h1>
        <p className="text-xl text-gray-600 mb-8">Access Denied</p>
        <div className="space-x-4">
          <button onClick={() => navigate(-1)} className="bg-gray-600 text-white px-6 py-3 rounded-lg">Go Back</button>
          <button onClick={logout} className="bg-red-600 text-white px-6 py-3 rounded-lg">Logout</button>
        </div>
      </div>
    </div>
  );
};

const AppRouter: React.FC = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <Routes>
      {/* ============================================ */}
      {/* PUBLIC ROUTES                                */}
      {/* ============================================ */}
      <Route path="/" element={<MarketingPageWrapper />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/investor" element={<InvestorPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/delete-data" element={<DeleteDataPage />} />
      <Route path="/blogs" element={<BlogPublicList />} />
      <Route path="/blog/:slug" element={<BlogPublicDetail />} />
      <Route path="/case-studies" element={<CaseStudyPublicList />} />
      <Route path="/case-studies/:slug" element={<CaseStudyPublicDetail />} />

      {/* ============================================ */}
      {/* QR / NFC / GS1 SCAN ROUTES                  */}
      {/* → AuthenticScanPage (beautiful auto-verify)  */}
      {/* ============================================ */}

      {/* NFC NDEF URL: http://jeptags.com/uid/UID:XXXX */}
      <Route path="/uid/:uid" element={<AuthenticScanPage />} />

      {/* GS1 Digital Link: /01/0614141XXXXX/21/TAGID */}
      <Route path="/01/:gtin/21/:tagId" element={<AuthenticScanPage />} />

      {/* GS1 NFC mirror: /01/GTIN/21/?uid=XXXX */}
      <Route path="/01/:gtin/21"  element={<AuthenticScanPage />} />
      <Route path="/01/:gtin/21/" element={<AuthenticScanPage />} />

      {/* GS1 GTIN only: /01/0614141XXXXX */}
      <Route path="/01/:gtin" element={<AuthenticScanPage />} />

      {/* Direct verify URL: /verify/TAGID */}
      <Route path="/verify/:tagId" element={<AuthenticScanPage />} />

      {/* verify with ?uid= query */}
      <Route path="/verify"  element={<AuthenticScanPage />} />
      <Route path="/verify/" element={<AuthenticScanPage />} />


      {/* ============================================ */}
      {/* CONSUMER MANUAL SCANNER ROUTES              */}
      {/* → ScanConsumerPage (full manual scanner)    */}
      {/* ============================================ */}
      <Route path="/consumer" element={<ScanConsumerPage />} />
      <Route path="/consumer/scan/:tagId" element={<ScanConsumerPage />} />
      <Route path="/consumer/verify/:tagId" element={<ScanConsumerPage />} />
      <Route path="/scanner" element={<Navigate to="/consumer" replace />} />

      {/* ============================================ */}
      {/* CONSUMER OTHER ROUTES                        */}
      {/* ============================================ */}
      <Route path="/consumer/my-entries" element={<MyEntriesPage />} />
      <Route path="/consumer/lottery-spin/:tagId/:campaignId" element={<LotterySpinPage />} />
      <Route path="/consumer/lottery-result/:entryId" element={<LotteryResultPage />} />
      <Route path="/consumer/lottery/:tagId" element={<ConsumerLotteryPage />} />
      <Route path="/consumer/lottery-test" element={<LotteryTestPage />} />
      <Route path="/consumer/lottery" element={<ViewLottery />} />
      <Route path="/consumer/stores" element={<ConsumerStoreMapPage />} />
      <Route path="/certificate" element={<CertificatePage />} />
      <Route path="/consumer/certificate" element={<CertificatePage />} />
      <Route path="/consumer/certificate/:tagId" element={<CertificatePage />} />
      <Route path="/consumer/lottery-certificate/:entryId" element={<LotteryCertificatePage />} />

      {/* ============================================ */}
      {/* BUSINESS ROUTES                              */}
      {/* ============================================ */}
      <Route path="/business/login" element={<BusinessLoginPage />} />
      <Route path="/business/register" element={<BusinessRegisterPage />} />
      <Route path="/business/pending-approval" element={<BusinessPendingApprovalPage />} />
      <Route path="/business-user/reset-password" element={<BusinessResetPasswordPage />} />
      <Route path="/business/reset-password" element={<BusinessResetPasswordPage />} />
      <Route path="/business/products/:productId/gs1" element={<ProductGS1Page />} />
      <Route path="/business/*" element={
        <ProtectedRoute requiredRole="business">
          <BusinessDashboard>
            <Routes>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="profile" element={<BusinessPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="stores" element={<StoresPage />} />
              <Route path="tags" element={<TagsPage />} />
              <Route path="batches" element={<BatchesPage />} />
              <Route path="qr-generator" element={<QRGeneratorPage />} />
              <Route path="batches/:batchId/activate" element={<ActivateTagsPage />} />
              <Route path="recall-batch" element={<RecallControlPage />} />
              <Route path="activate-tags" element={<ActivateTagsPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="marketing" element={<MarketingPage />} />
              <Route path="rewards" element={<RewardsPage />} />
              <Route path="supply-chain" element={<SupplyChainPage />} />
              <Route path="dashboard" element={<BusinessDashboardPage />} />
              <Route path="gs1-settings" element={<BusinessGS1Page />} />
              <Route path="shop" element={<BusinessShopPage />} />
              <Route path="blockchain" element={<BlockchainPlansPage />} />
              <Route path="*" element={<Navigate to="/business/overview" replace />} />
            </Routes>
          </BusinessDashboard>
        </ProtectedRoute>
      } />

      {/* ============================================ */}
      {/* ADMIN ROUTES                                 */}
      {/* ============================================ */}
      <Route path="/admin/*" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard>
            <Routes>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<AdminOverviewPage />} />
              <Route path="businesses" element={<BusinessManagementPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="system" element={<SystemHealthPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="tags" element={<TagsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/admin/overview" replace />} />
            </Routes>
          </AdminDashboard>
        </ProtectedRoute>
      } />

      {/* ============================================ */}
      {/* STORE ROUTES                                 */}
      {/* ============================================ */}
      <Route path="/store/login" element={<StoreLogin />} />
      <Route path="/store/dashboard" element={<StoreDashboard />} />
      <Route path="/store/activate" element={<StoreActivateTag />} />

      {/* ============================================ */}
      {/* REDIRECTS & FALLBACK                         */}
      {/* ============================================ */}
      <Route path="/dashboard" element={<Navigate to="/business/overview" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/business/overview" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </BrowserRouter>
);

export default AppRouter;